import { Link } from 'react-router-dom';
import { useSite } from '@/context/SiteContext';
import { getIcon } from '@/lib/icons';
import { MAIN_NAV } from '@/constants/nav';
import { Button } from '@/components/ui/button';
import { Download, MapPin, Phone, Mail } from 'lucide-react';

const Footer = () => {
    const { settings } = useSite();
    const socials = settings?.social_links || [];
    const offices = settings?.offices || [];

    return (
        <footer
            className="mt-16 bg-[hsl(var(--primary))] text-primary-foreground"
            data-testid="site-footer"
        >
            <div className="container-wide py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                {/* About */}
                <div>
                    <div className="flex items-center gap-3 mb-4">
                        {settings?.logo_url && (
                            <img
                                src={settings.logo_url}
                                alt="VVVSI"
                                className="h-12 w-12 object-contain rounded bg-white p-1"
                            />
                        )}
                        <div>
                            <div className="font-display font-semibold text-lg">VVVSI</div>
                            <div className="text-[11px] opacity-80">Trust</div>
                        </div>
                    </div>
                    <p className="text-sm opacity-90 leading-relaxed">
                        {settings?.about_short ||
                            'A charitable trust for employment, career guidance, education, health and social welfare across Tamil Nadu & Puducherry.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {(settings?.compliance_badges || []).map((b) => (
                            <span
                                key={b}
                                className="text-[10px] font-semibold px-2 py-1 rounded bg-white/10 border border-white/20"
                            >
                                {b}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Quick Links */}
                <div>
                    <div className="font-display font-semibold mb-4">Quick Links</div>
                    <ul className="space-y-2 text-sm">
                        {MAIN_NAV.slice(0, 8).map((n) => (
                            <li key={n.to}>
                                <Link
                                    to={n.to}
                                    className="opacity-90 hover:opacity-100 hover:underline"
                                    data-testid={`footer-link-${n.to.replace(/\//g, '')}`}
                                >
                                    {n.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Contact */}
                <div>
                    <div className="font-display font-semibold mb-4">Contact</div>
                    <ul className="space-y-3 text-sm">
                        {offices.map((o, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 mt-0.5 shrink-0 opacity-80" />
                                <div className="opacity-90">
                                    <div className="font-medium">{o.label}</div>
                                    <div className="text-[13px] opacity-80">
                                        {o.address_lines.join(', ')}, {o.city} {o.pincode}
                                    </div>
                                </div>
                            </li>
                        ))}
                        {settings?.contact_phone && (
                            <li className="flex items-center gap-2">
                                <Phone className="h-4 w-4 opacity-80" />
                                <a href={`tel:${settings.contact_phone}`} className="hover:underline">
                                    {settings.contact_phone}
                                </a>
                            </li>
                        )}
                        {settings?.contact_email && (
                            <li className="flex items-center gap-2">
                                <Mail className="h-4 w-4 opacity-80" />
                                <a href={`mailto:${settings.contact_email}`} className="hover:underline">
                                    {settings.contact_email}
                                </a>
                            </li>
                        )}
                    </ul>
                </div>

                {/* Social + Brochure */}
                <div>
                    <div className="font-display font-semibold mb-4">Stay Connected</div>
                    <div className="flex items-center gap-3 mb-6">
                        {socials.map((s) => {
                            const Icon = getIcon(s.platform);
                            return (
                                <a
                                    key={s.platform}
                                    href={s.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                                    aria-label={s.platform}
                                    data-testid={`footer-social-${s.platform}`}
                                >
                                    <Icon className="h-4 w-4" />
                                </a>
                            );
                        })}
                    </div>
                    {settings?.brochure_url && (
                        <a
                            href={settings.brochure_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="footer-brochure-download"
                        >
                            <Button
                                variant="secondary"
                                className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
                            >
                                <Download className="h-4 w-4 mr-1.5" />
                                Download Brochure
                            </Button>
                        </a>
                    )}
                </div>
            </div>

            <div className="border-t border-white/10 py-4">
                <div className="container-wide flex flex-col sm:flex-row items-center justify-between gap-2 text-xs opacity-80">
                    <div>
                        © {new Date().getFullYear()} {settings?.trust_name || 'VVVSI'}. All rights reserved.
                    </div>
                    <div className="flex items-center gap-4">
                        <span>Registered Charitable Trust</span>
                        <span>|</span>
                        <span>Website: {settings?.website || 'www.vvvsi.com'}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
